import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, Modal, Radio, Spin, message } from "antd";
import { Amount } from "fin-web-components";
import { formatApiDateTime } from "../utils/Backend";
import { resolveAppMediaUrl } from "../utils/resolveAppMediaUrl";
import type { AccountDataScope } from "./AccountDataScope";
import { StoreOrderApi } from "./StoreOrderApi";
import type { AppMyStoreOrderRow, PreparePayResult, QueryPayStatusResult } from "./StoreOrderType";
import {
  STORE_ORDER_PAY_DEADLINE_MS,
  formatPayCountdown,
  isStoreOrderPaid,
  parseStoreOrderCreatedAtMs,
  resolveStoreOrderServicePath,
  resolveStoreOrderListPath,
} from "./storeOrderDetailUtils";
import "./StoreOrderDetailPanel.css";

export type StoreOrderDetailPanelProps = {
  spaceId: string;
  orderId: string;
  scope: AccountDataScope;
  onMissingParams?: () => void;
};

function MetaRow(props: { label: string; value: string; mono?: boolean }): JSX.Element {
  const { label, value, mono } = props;
  return (
    <div className="store-order-detail__meta-row">
      <span className="store-order-detail__meta-label">{label}</span>
      <span
        className={
          mono
            ? "store-order-detail__meta-value store-order-detail__meta-value--mono"
            : "store-order-detail__meta-value"
        }
      >
        {value}
      </span>
    </div>
  );
}

function statusClass(code: string): string {
  if (code === "unpaid" || code === "paid_active") {
    return "store-order-detail__status";
  }
  return "store-order-detail__status store-order-detail__status--muted";
}

export function StoreOrderDetailPanel(props: StoreOrderDetailPanelProps): JSX.Element | null {
  const { spaceId, orderId, scope, onMissingParams } = props;
  const navigate = useNavigate();
  const [order, setOrder] = useState<AppMyStoreOrderRow | null>(null);
  const [prep, setPrep] = useState<PreparePayResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState(false);
  const [payRemainMs, setPayRemainMs] = useState(STORE_ORDER_PAY_DEADLINE_MS);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [afterOpen, setAfterOpen] = useState(false);
  const [pendingViewOpen, setPendingViewOpen] = useState(false);
  const [afterForm] = Form.useForm<{ afterType: "refund" | "extend_service"; reason: string }>();
  const cdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goService = useCallback(() => {
    if (!order) return;
    navigate(resolveStoreOrderServicePath(order, scope));
  }, [navigate, order, scope]);

  const goOrderList = useCallback(() => {
    navigate(resolveStoreOrderListPath(scope));
  }, [navigate, scope]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [spaceId, orderId]);

  useEffect(() => {
    if (!spaceId || !orderId) {
      message.error("参数缺失");
      onMissingParams?.();
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const detailRes = await StoreOrderApi.getMyOrderDetail({ spaceId, orderId });
      if (cancelled) return;
      if (typeof detailRes === "string") {
        setLoading(false);
        return;
      }
      const row = detailRes.order;
      setOrder(row);
      const alreadyPaid = isStoreOrderPaid(row);
      setPaid(alreadyPaid);
      if (alreadyPaid) {
        setLoading(false);
        return;
      }
      const prepRes = await StoreOrderApi.preparePay(
        {
          spaceId,
          orderId,
          payScene: "web_page",
          returnUrl: typeof window !== "undefined" ? window.location.href : undefined,
          clientUserAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        },
        { silent: true }
      );
      if (cancelled) return;
      if (typeof prepRes !== "string") {
        setPrep(prepRes);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [spaceId, orderId, onMissingParams]);

  useEffect(() => {
    if (!spaceId || !orderId || paid || !order) return;
    pollRef.current = setInterval(() => {
      void (async () => {
        const res = await StoreOrderApi.queryPayStatus({ spaceId, orderId });
        if (typeof res === "string") return;
        const r = res as QueryPayStatusResult;
        if (r.paid) {
          if (pollRef.current) clearInterval(pollRef.current);
          setPaid(true);
          setOrder((prev) =>
            prev
              ? {
                  ...prev,
                  displayStatus: "paid_active",
                  displayStatusLabel: "已支付",
                  payTime: formatApiDateTime(r.payTime),
                }
              : prev
          );
        }
      })();
    }, 1500);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [spaceId, orderId, paid, order]);

  useEffect(() => {
    if (paid || !order) {
      if (cdRef.current) clearInterval(cdRef.current);
      return;
    }
    const createdMs = parseStoreOrderCreatedAtMs(order.createdAt);
    if (createdMs == null) return;
    const deadlineMs = createdMs + STORE_ORDER_PAY_DEADLINE_MS;
    const tick = () => {
      setPayRemainMs(Math.max(0, deadlineMs - Date.now()));
    };
    tick();
    cdRef.current = setInterval(tick, 1000);
    return () => {
      if (cdRef.current) clearInterval(cdRef.current);
    };
  }, [paid, order]);

  const reloadOrder = useCallback(async () => {
    const detailRes = await StoreOrderApi.getMyOrderDetail({ spaceId, orderId });
    if (typeof detailRes === "string") return;
    setOrder(detailRes.order);
  }, [spaceId, orderId]);

  const submitAfter = async () => {
    if (!order) return;
    try {
      const v = await afterForm.validateFields();
      const res = await StoreOrderApi.submitAfterSale({
        spaceId: order.spaceId,
        orderId: order.orderId,
        afterType: v.afterType,
        reason: v.reason.trim(),
      });
      if (typeof res === "string") return;
      message.success("已提交售后申请");
      setAfterOpen(false);
      afterForm.resetFields();
      await reloadOrder();
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
    }
  };

  if (!spaceId || !orderId) {
    return null;
  }

  const redirectUrl = prep?.redirectUrl?.trim() ?? "";
  const posterSrc = resolveAppMediaUrl(order?.productPosterUrl?.trim() || null);
  const tierText = order?.subProductName?.trim()
    ? `${order.serviceTierLabel || order.serviceTier} · ${order.subProductName.trim()}`
    : order?.serviceTierLabel?.trim() || order?.serviceTier || "—";
  const payExpired = !paid && payRemainMs <= 0;
  const pendingAfterSale = order?.pendingAfterSale ?? null;
  const showAfter =
    paid &&
    order &&
    order.displayStatus !== "refunded" &&
    order.displayStatus !== "completed" &&
    order.displayStatus !== "unpaid";
  const showAfterApply = showAfter && !pendingAfterSale;
  const showAfterPending = showAfter && !!pendingAfterSale;

  return (
    <div className="store-order-detail">
      <div className="store-order-detail__shell">
        <Spin spinning={loading}>
          {order ? (
            <div className="store-order-detail__card">
              <header className="store-order-detail__head">
                <h1 className="store-order-detail__title">订单详情</h1>
                <span className={statusClass(order.displayStatus)}>
                  {order.displayStatusLabel || "—"}
                </span>
              </header>

              {posterSrc ? (
                <img src={posterSrc} alt="" className="store-order-detail__poster" />
              ) : null}

              <p className="store-order-detail__product-name">{order.productName?.trim() || "—"}</p>

              <div className="store-order-detail__meta-grid">
                <MetaRow label="规格" value={tierText} />
                <MetaRow label="所属空间" value={order.spaceName?.trim() || "—"} />
                <MetaRow label="订单号" value={order.orderId} mono />
                <MetaRow label="下单时间" value={formatApiDateTime(order.createdAt) || "—"} mono />
                {paid && order.payTime ? (
                  <MetaRow label="支付时间" value={formatApiDateTime(order.payTime)} mono />
                ) : null}
              </div>

              <div className="store-order-detail__amounts">
                <div className="store-order-detail__amount-line">
                  <span className="store-order-detail__amount-label">商品价格</span>
                  <span className="store-order-detail__amount-value">
                    <Amount value={order.productPriceYuan} fontSize={14} fontWeight={400} />
                  </span>
                </div>
                <div className="store-order-detail__amount-line">
                  <span className="store-order-detail__amount-label">{paid ? "实付金额" : "应付金额"}</span>
                  <span className="store-order-detail__amount-value store-order-detail__amount-value--pay">
                    <Amount value={order.payAmountYuan} fontSize={16} fontWeight={400} />
                  </span>
                </div>
              </div>

              <div className="store-order-detail__actions">
                {paid ? (
                  <Button type="primary" className="store-order-detail__btn" onClick={goService}>
                    进入服务
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    className="store-order-detail__btn"
                    href={redirectUrl || undefined}
                    disabled={!redirectUrl || payExpired}
                  >
                    立即支付 {formatPayCountdown(payRemainMs)}
                  </Button>
                )}

                <div className="store-order-detail__actions-foot">
                  <button type="button" className="store-order-detail__sub-link" onClick={goOrderList}>
                    前往订单列表
                  </button>
                  {showAfterApply ? (
                    <button
                      type="button"
                      className="store-order-detail__sub-link"
                      onClick={() => {
                        afterForm.setFieldsValue({ afterType: "refund", reason: "" });
                        setAfterOpen(true);
                      }}
                    >
                      申请售后
                    </button>
                  ) : null}
                  {showAfterPending ? (
                    <button
                      type="button"
                      className="store-order-detail__sub-link store-order-detail__sub-link--pending"
                      onClick={() => setPendingViewOpen(true)}
                    >
                      售后申请处理中
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </Spin>
      </div>

      <Modal
        title="申请售后"
        open={afterOpen}
        onCancel={() => setAfterOpen(false)}
        onOk={() => void submitAfter()}
        okText="提交"
        cancelText="取消"
        destroyOnClose
        className="my-orders-modal"
      >
        <Form form={afterForm} layout="vertical">
          <Form.Item name="afterType" initialValue="refund">
            <Radio.Group>
              <Radio value="refund">我要退款</Radio>
              <Radio value="extend_service">我要补偿服务时间</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="reason"
            rules={[
              { required: true, message: "请填写原因" },
              { max: 300, message: "300字以内" },
            ]}
          >
            <Input.TextArea placeholder="原因描述（300字以内）" rows={4} maxLength={300} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="售后申请"
        open={pendingViewOpen}
        onCancel={() => setPendingViewOpen(false)}
        footer={
          <Button onClick={() => setPendingViewOpen(false)} style={{ minWidth: 120 }}>
            关闭
          </Button>
        }
        destroyOnClose
        className="my-orders-modal"
      >
        {pendingAfterSale ? (
          <>
            <p className="store-order-detail__after-pending-tip">
              您的售后申请正在处理中..工作人员将会尽快联系您
            </p>
            <div className="store-order-detail__after-view">
              <MetaRow label="售后类型" value={pendingAfterSale.afterTypeLabel || "—"} />
              <MetaRow label="提交时间" value={formatApiDateTime(pendingAfterSale.createdAt) || "—"} mono />
              <div className="store-order-detail__after-reason-block">
                <span className="store-order-detail__meta-label">申请原因</span>
                <p className="store-order-detail__after-reason-text">{pendingAfterSale.reason?.trim() || "—"}</p>
              </div>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
}
